import { Paginated } from "@feathersjs/feathers/lib";
import { authStore, rootStore } from "app";
import { action, computed, makeObservable, observable, reaction, runInAction } from "mobx";
import { DataType, IChoice, IFile, IProgress, IQuiz, IStep, ITask, IVideo, Services, StepType, UserType } from "types";
import { Course, Data, File, Quiz, Video } from ".";
import client from "../feathers";
import i18next from "../i18n";

export class Step extends Data<IStep> implements IStep {

  content:    string   = null;
  course_id:  number   = null;
  mandatory:  boolean  = null;
  next_id?:   number   = null;
  parent_id?: number   = null;
  prev_id?:   number   = null;
  quiz_id?:   number   = null;
  video_id?:  number   = null;
  title:      string   = null;
  name:       string   = null;
  block_ids:  string[] = [];
  quiz:       Quiz     = null;
  video:      Video    = null;
  documents?: File[]   = [];
  is_grid?:   boolean  = null;
  is_sub?:    boolean  = null;
  thumbnail?: File     = null;

  public static readonly split_char: string = '\u0001';
  private block_id_counter = 0;
  
  constructor(data: IStep, public course: Course) {
    // The super constructr takes care of references to the service, validation rules
    // and intialized the base members with mobx decorators
    super(DataType.Step, data);

    // Since Mobx cannot track derived class objects automatically,
    // the decorators have to be provided manually
    makeObservable(this, {
      content:   observable,
      course_id: observable,
      mandatory: observable,
      next_id:   observable,
      parent_id: observable,
      prev_id:   observable,
      quiz_id:   observable,
      video_id:  observable,
      quiz:      observable,
      video:     observable,
      title:     observable,
      name:      observable,
      block_ids: observable,
      documents: observable,
      is_grid:   observable,
      is_sub:    observable,
      thumbnail: observable,

      isChapter:    computed,
      isQuiz:       computed,
      isVideo:      computed,
      isGrid:       computed,
      isSub:        computed,
      getType:      computed,
      getBlocks:    computed,
      getPrevStep:  computed,
      getNextStep:  computed,
      isCompleted:  computed,
      isLast:       computed,
      getDocuments: computed,
      getGridSteps: computed,

      addBlock:        action,
      removeBlock:     action,
      updateBlock:     action,
      rearrangeBlocks: action,
      next:            action,
      addDocument:     action,
      removeDocument:  action,
      addGridStep:     action,
      removeGridStep:  action,
    });

    // Initialize data
    // While the super constructor will call this once,
    // the default values given to members will overwrite this so it has to be called again here.
    this.fromData(data);

    // Generate unique IDs for each block that can be used as react keys
    for(const b in this.getBlocks) this.block_ids.push(this.id + "_" + this.block_id_counter++);

    if(this.isQuiz)  this.loadQuiz();
    if(this.isVideo) this.loadVideo();
    
    // Update block ids when creating a new step
    reaction(() => this.id, () => {
      if(this.id) for(const b in this.getBlocks) {
        this.block_ids = [];
        this.block_ids.push(this.id + "_" + this.block_id_counter++);
      }
    });
  }
  /*******************************************************************************************************************************************
   * Database Access
   ********************************************************************************************************************************************/
 
  /**
   * Load this step's quiz with all related content, i.e. tasks and choices. If the 
   * current user is an editor, this will load all quiz content.
   * If the current user is a participant or manager, this will generate a
   * quiz and only transmit data relevant for taking the quiz. 
   * 
   * @return <true> if quiz generation/loading was successful, false otherwise. 
   */
  public loadQuiz = async (generate?: boolean): Promise<boolean> => {
    if(!this.quiz_id) return false;

    try {
      const quiz: IQuiz = await client.service(Services.Quiz).get(this.quiz_id);

      const isEditor = rootStore.authStore.getCurUserType === UserType.Editor
      if(!isEditor || generate) {
        // A participant can only generate a quiz
        const tasks: ITask[] = await client.service(Services.Quiz).generate({quiz_id: this.quiz_id});
        quiz.tasks = tasks || [];
      } else {
        // An editor can load all tasks and choices
        const tasks: Paginated<ITask> = await client.service(Services.Task).find({
          query: { quiz_id: quiz.id }
        });
        const choices: Paginated<IChoice> = await client.service(Services.Choice).find({
          query: { quiz_id: quiz.id }
        });
        quiz.tasks = tasks.data || [];
        quiz.tasks.forEach((t) => {
          t.choices = choices.data.filter(c => c.task_id === t.id);
        });
      }
      runInAction(() => {
        this.quiz = Quiz.create(quiz, this.course);
        if(!isEditor && !quiz.is_control_quiz) this.quiz.shuffle();
      });
      return true;
    } catch (err) {
      console.error("Failed to load quiz content", err);
      return false;
    }
  }

  public loadVideo = async () => {
    if(!this.video_id)  return;

    try {
      const result:  IVideo = await client.service(Services.Video).get(this.video_id);    
      runInAction(() => this.video = Video.create(result));
      return true;
    } catch (err) {
      console.error("Failed to load video content", err);
      return false;
    }
  }

  public loadDocuments = async () => {
    if(this.getDocuments.length) return;

    try {
      // Load document list
      const document_list: Paginated<IFile> = await client.service(Services.Document).find({
        query: { 
          course_id:    this.course_id,
          step_id:      this.id,
          $or: [{is_thumbnail: false}, {is_thumbnail: null}]
        }
      });
      runInAction(() => {
        this.documents = [];
        document_list.data.map( fd => this.documents.push( File.create(fd) ));
      });

      // Load thumbnail
      const thumbnail: Paginated<IFile> = await client.service(Services.Document).find({
        query: { 
          course_id:    this.course_id,
          step_id:      this.id,
          is_thumbnail: true,
        }
      });
      if(thumbnail.total) {
        runInAction(() => {
          this.thumbnail = File.create(thumbnail.data[0]);
        });
      }
      return true;
    } catch (err) {
      console.error("Failed to load documents for step", err);
      return false;
    }
  }

  /**
   * Record step progress. Note that this is not needed for quizzes as validating a quiz
   * automatically records progress. 
   * Progress validation, i.e. checking for mandatory steps etc., is done in the backend. 
   */
  recordProgress = async (): Promise<boolean> => {
    try {
      const progress: IProgress = await client.service(Services.Progress).create({
        attempts:   1,
        course_id:  this.course_id,
        step_id:    this.id,
        user_id:    authStore.getCurUserID,
      });

      runInAction(() => {
        this.course.course_progress.push({
            attempts:  Number(progress.attempts),
            completed: progress.completed && new Date(progress.completed),
            course_id: Number(progress.course_id),
            step_id:   Number(progress.step_id),
            user_id:   Number(progress.user_id),
        });
        this.course.sortProgress();
        this.course.progress = Math.round((this.course.course_progress.length / this.course.steps.length)*100);
      });
      
      return Boolean(progress);
    } catch(err) {
      console.error("Failed to record user progress", err);
      return false;
    }
  }

  /*******************************************************************************************************************************************
   * Overwrites
   ********************************************************************************************************************************************/

  public static get = async (id?: number): Promise<Step | Step[]> => {
    try {
      if(id) {
        return Step.create(await client.service(Services.Step).get(id), null);
      } else {
        const Stepes: Paginated<IStep> = await client.service(Services.Step).find();
        if(!Stepes.total) return [];
        return Stepes.data.map(u => Step.create(u, null));
      }
    } catch (err) {
      console.error("Failed to load Stepes", err);
      return null;
    }
  }

  public static create(data: IStep, course?: Course): Step {
    return new Step(data, course);
  }

  protected fromData(data: IStep): void {
    super.fromData(data);
    this.content   = data.content;
    this.mandatory = data.mandatory;
    this.course_id = data.course_id && Number(data.course_id);
    this.next_id   = data.next_id   && Number(data.next_id);
    this.parent_id = data.parent_id && Number(data.parent_id);
    this.prev_id   = data.prev_id   && Number(data.prev_id);
    this.quiz_id   = data.quiz_id   && Number(data.quiz_id);
    this.video_id  = data.video_id  && Number(data.video_id);
    this.title     = data.title;
    this.name      = data.name;
    this.is_grid   = Boolean(data.is_grid);
    this.is_sub    = Boolean(data.is_sub);
  }

  public toData(): any {
    return {
      ...super.toData(),
      content:   this.content   || null,
      course_id: this.course_id || null,
      mandatory: this.mandatory || null,
      next_id:   this.next_id   || null,
      parent_id: this.parent_id || null,
      prev_id:   this.prev_id   || null,
      quiz_id:   this.quiz_id   || null,
      video_id:  this.video_id  || null,
      title:     this.title     || null,
      name:      this.name      || null,
      is_grid:   this.is_grid   || null,
      is_sub:    this.is_sub    || null,
    };
  }

  public toPatchData(patch: Partial<IStep>): any {
    return {
      ...super.toPatchData(patch),
      content:   "content"   in patch ? this.content   : undefined,
      course_id: "course_id" in patch ? this.course_id : undefined,
      mandatory: "mandatory" in patch ? this.mandatory : undefined,
      next_id:   "next_id"   in patch ? this.next_id   : undefined,
      parent_id: "parent_id" in patch ? this.parent_id : undefined,
      prev_id:   "prev_id"   in patch ? this.prev_id   : undefined,
      quiz_id:   "quiz_id"   in patch ? this.quiz_id   : undefined,
      video_id:  "video_id"  in patch ? this.video_id  : undefined,
      title:     "title"     in patch ? this.title     : undefined,
      name:      "name"      in patch ? this.name      : undefined,
      is_grid:   "is_grid"   in patch ? this.is_grid   : undefined,
      is_sub:    "is_sub"    in patch ? this.is_sub    : undefined,
    };
  }

  /*******************************************************************************************************************************************
   * Getter
   ********************************************************************************************************************************************/

  public get canEdit(): boolean {
    return true;
  }

  public get isChapter(): boolean {
    return !this.parent_id;
  }

  public get isQuiz(): boolean {
    return Boolean(this.quiz_id);
  }

  public get isVideo(): boolean {
    return Boolean(this.video_id);
  }

  public get isGrid(): boolean {
    return this.is_grid;
  }

  /**
   * @return <true> if this is a sub step of a grid
   */
  public get isSub(): boolean {
    return Boolean(this.is_sub);
  }

  public get getType(): StepType {
    if(this.is_grid)  return StepType.Grid;
    if(this.isQuiz)   return StepType.Quiz;
    if(this.isVideo)  return StepType.Video;
    return StepType.Read;
  }

  public get getBlocks(): string[] {
    return this.content ? this.content.split(Step.split_char).filter(Boolean) : [];
  }

  public get getDocuments(): File[] {
    return (this.documents||[]).slice();
  }

  public get getThumbnail(): File | null {
    return this.thumbnail;
  }

  /**
   * @return <true> if the step is accessible for the current participant/manager user,
   *         i.e. if the step is either already completed, or there is no 
   *         mandatory step inbetween @this.course.curStep and the @this
   */
  public get isAccessible(): boolean {
    if(!this.course) return false;

    const this_idx      = this.course.steps.findIndex(s => s.id === this.id);
    const mandatory_idx = this.course.getNextMandatoryStepIndex;

    return (mandatory_idx === -1 || mandatory_idx >= this_idx);
  }

  /**
   * @return the Step object of the next step in @this.course's step structure as
   *         indicated by @this.next_id or null if that does not exist. 
   */
  public get getNextStep(): Step {
    let idx = this.course.steps.findIndex(s => Number(s.id) === Number(this.id));
    if(this.isGrid) {
      while((idx+1) < this.course.steps.length && this.course.steps[idx+1].isSub)
        idx++;
    }
    let next = (idx+1) < this.course.steps.length ? this.course.steps[idx+1] : null;
    while(next && next.is_sub) next = next.getNextStep;
    return next;
  }

  public get getPrevStep(): Step {
    let idx = this.course.getStepIndex(this.id);
    if((idx-1) >= 0 && this.course.steps[idx-1]?.isSub)
      idx = idx - this.course.steps[idx-1].getParent.getGridSteps.length;
    return (idx-1) >= 0 ? this.course.steps[idx-1] : null;
  }

  public get isCompleted(): boolean {
    if(!this.course.course_progress) return false;
    const progress = this.course.course_progress.find(p => Number(p.step_id) === Number(this.id));
    return progress && Boolean(progress.completed);
  }

  public get isLast(): boolean {
    return !this.getNextStep;
  }

  public get isIntro(): boolean {
    return !this.prev_id && !this.parent_id;
  }

  public get getGridSteps(): Step[] {
    if(this.getType !== StepType.Grid) return [];
    return this.course?.steps
      .filter(s => s.parent_id === this.id && s.isSub)
      .sort((l, r) => l.created_at.getTime() - r.created_at.getTime())
      || [];
  }

  public get getParent(): Step | null {
    if(!this.parent_id) return null;
    return this.course?.steps.find(s => s.id === this.parent_id) || null;
  }

  /*******************************************************************************************************************************************
   * Setter
   ********************************************************************************************************************************************/

  /*******************************************************************************************************************************************
   * Actions
   ********************************************************************************************************************************************/

  /**
   * Adds a new block to the end of the list. If @content is not provided,
   * an empty paragraph is created.
   */
  addBlock = async (content?: string) => {
    this.content += Step.split_char + `<p>${content || ""}</p>`;
    this.block_ids.push(this.id + "_" + this.block_id_counter++);
    try {
      await this.save({content: this.content});
    } catch (err) {
      console.error("Failed to add block", err);
    }
  }

  /**
   * Remove block @idx
   */
  removeBlock = async (idx: number) => {
    this.content   = this.getBlocks.filter((b, _idx) => _idx !== idx).join(Step.split_char);
    this.block_ids = this.block_ids.filter((b, _idx) => _idx !== idx);

    try {
      await this.save({content: this.content});
    } catch (err) {
      console.error("Failed to remove block", err);
    }
  }

  /**
   * Updatest the rich text content of a specific block.
   * 
   * @param idx     Index of block to be updated
   * @param content New Rich text string of the block @idx
   */
  updateBlock = async (idx: number, content: string) => {
    const blocks: string[] = this.getBlocks;
    blocks[idx] = content || '<p></p>';
    this.content = blocks.join(Step.split_char);
    try {
      await this.save({content: this.content});
    } catch (err) {
      console.error("Failed to update block", err);
    }
  }

  /**
   * Rearranges the block order of the step.
   * 
   * @param old_idx Index of block to be moved
   * @param new_idx Target index where block is moved
   */
  rearrangeBlocks = async (old_idx: number, new_idx: number) => {
    const blocks: string[] = this.getBlocks;
    blocks.splice(new_idx, 0, ...blocks.splice(old_idx, 1));
    this.block_ids.splice(new_idx, 0, ...this.block_ids.splice(old_idx, 1));
    this.content = blocks.join(Step.split_char);
    try {
      await this.save({content: this.content});
    } catch (err) {
      console.error("Failed to rearrange block", err);
    }
  }

  /**
   * Add document take a dropped file and uploads it.
   * After a complete upload to the storage. The course also connects it to
   * Does not affect athen_id, thus, links to the shop are not(!) copied.
   */
  addDocument = async (document: File): Promise<any> => {
    try {
      document.course_id = this.course.id;
      document.step_id   = this.id;
      const res = await document.upload();
      runInAction(() => {
        if(document.is_thumbnail) this.thumbnail = document;
        else this.documents = [...this.getDocuments, document];
      });
      return;
    } catch (err) {
      console.error("Failed to upload document to step", err);
      return null;
    }
  }

  removeDocument = async (document: File): Promise<boolean> => {
    try {
      await document.remove();
      runInAction(() => {
        if(document.is_thumbnail) this.thumbnail = null;
        else this.documents = this.documents.filter( f => f.id != document.id );
      });
      return true;
    } catch (err) {
      console.error("Failed to remove document from step", err);
      return false;
    }
  }
  
  /**
   * Turn current step into a quiz by creating an empty quiz in the db
   */
  public makeQuiz = async () => {
    if(this.quiz_id) return;

    try {
      const quiz: IQuiz = await client.service(Services.Quiz).create({
        course_id:   this.course_id,
        num_correct: 0,
        num_tasks:   0,
        step_id:     this.id,
        title:       i18next.t("quiz"),
      });
      runInAction(() => {
        this.quiz    = Quiz.create(quiz, this.course);
        this.quiz_id = quiz.id;
      })
    } catch(err) {
      console.error("Failed to create quiz", err);
      return;
    }
  }
  /**
   * Turn current step into a video step by creating an empty video in the db
   */
  public makeVideo = async () => {

    if(this.video_id) return;
    try {
      const video: IVideo = await client.service(Services.Video).create({
        course_id:   this.course_id,
        step_id:     this.id,
        vimeo_id:    null,
        chapters:    [],
        title:       i18next.t("video"),
      });
      
      runInAction(() => {
        this.video    = Video.create(video);
        this.video_id = video.id;
      })
    } catch(err) {
      console.error("Failed to create video", err);
      return;
    }
  }

  /**
   * Turn current step into a grid step by creating an empty array of video IDs
   */
  public makeGrid = async () => {
    try {
      this.set("is_grid", true, true);
    } catch(err) {
      console.error("Failed to create grid", err);
    }
  }

  /**
   * Sets @this.course.curStep to the next step in the step structure if possible. 
   * Takes care of recording progress.
   * 
   * @return <true> if successful or if the end of the course was reached, <false> if
   *         recording progress failed. 
   */
  public next = async (preview?: boolean): Promise<boolean> => {
    return this.course.next(preview);
  }

  public addGridStep = async (type: StepType): Promise<boolean> => {
    try {
      const step: Step = await this.course.addStep(this.id, type, true);
      if(step) this.course.setCurStep(step);
      else throw new Error("Returned step empty");
    } catch(err) {
      console.error("Failed to add step", err);
      return false;
    }
    return true;
  }

  public removeGridStep = async(idx: number): Promise<boolean> => {
    if(!this.getGridSteps[idx]) return false;
    return this.course.removeStep(this.getGridSteps[idx].id);
  }
}
